import dayjs from 'dayjs';
import { CouponIProps } from '@/types/credits';
import { DISCOUNT_TYPE, PREREQUISITE, EXPIRE_TYPE } from '@/constants/credits';
import type { TipPorps } from '@/types/credits';

/**
 * 是否展示优惠券购买规则
 * *若优惠券折扣类型为固定金额,优惠券金额大于最低购买金额、或折扣类型是固定金额且未设置最低购买金额,则在规则里展示该说明
 * *若优惠券折扣类型为固定金额,优惠券金额小于或等于最低购买金额、或折扣类型是百分比,则在规则里隐藏该说明
 * @returns
 */
export const isShowCouponPurchaseRule = (item: CouponIProps): boolean => {
    // 只处理优惠券折扣类型为固定金额
    if (DISCOUNT_TYPE.FIXED_AMOUNT != item.discountType) {
        return false;
    }
    // 按数量购买
    if (PREREQUISITE.QUANTITY == item.prerequisite) {
        return true;
    }
    // 未设置最低购买金额
    const minPurchaseAmout = item?.prerequisiteRange ? Number(item.prerequisiteRange) : 0;
    if (minPurchaseAmout == 0) {
        return true;
    }
    const amount = item?.ruleWebsite?.amount ? Number(item?.ruleWebsite?.amount) : 0;
    return amount > minPurchaseAmout;
};
/**
 * @description 获取到期提示文案
 * @param coupon
 * @returns
 */
export const getExpiredTip = (coupon: CouponIProps): TipPorps | null => {
    const { validStartTime, validEndTime, expiredType, expiredFlag, expiredDay } = coupon;
    let tip;
    if (!expiredFlag) {
        tip = null;
    }
    if (EXPIRE_TYPE.DAY === expiredType && !!expiredDay) {
        tip = {
            key: 'coupon_rule_valid_time',
            value: `${expiredDay / (24 * 60)}`,
        };
    } else if (EXPIRE_TYPE.TIME === expiredType) {
        const txt =
            validStartTime && validEndTime
                ? `${dayjs(Number(validStartTime)).format('YYYY.MM.DD HH:mm:ss')}-${dayjs(Number(validEndTime)).format('YYYY.MM.DD HH:mm:ss')}`
                : '';
        tip = txt
            ? {
                  key: 'coupon_rule_valid_date',
                  value: `${txt}`,
              }
            : null;
    } else {
        tip = null;
    }
    return tip;
};

/**
 * @description 获取到限制购买提示文案
 * @param coupon
 * @returns
 */
export const getLimitPurchaseTip = (coupon: CouponIProps): TipPorps | null => {
    const { prerequisite, prerequisiteRange } = coupon;
    if (PREREQUISITE.PRICES === prerequisite) {
        return {
            key: 'coupon_rule_min_purchase',
            value: `${coupon?.ruleWebsite?.currencySymbol || coupon?.currencySymbol}${prerequisiteRange}`,
        };
    } else if (PREREQUISITE.QUANTITY === prerequisite) {
        return {
            key: 'coupon_rule_min_quantity',
            value: `${prerequisiteRange}`,
        };
    } else {
        return null;
    }
};

/**
 * 展示积分规则
 */
export const showCouponDetailTip = (coupon: CouponIProps): TipPorps[] => {
    const textArray: TipPorps[] = [];
    textArray.push({
        key: 'coupon_rule_store',
        value: `${coupon?.ruleWebsite?.appid || coupon?.appid}`,
    });
    textArray.push({
        key: 'coupon_rule_one_code',
    });
    if (coupon?.goodId) {
        textArray.push({
            key: 'coupon_rule_product',
            value: `${coupon?.goodTitle}`,
        });
    }
    if (isShowCouponPurchaseRule(coupon)) {
        // Purchasing an item for less than the coupon(s), will forfeit the difference.
        textArray.push({
            key: 'coupon_rule_purchase_item',
        });
    }
    const expiredTip = getExpiredTip(coupon);
    if (expiredTip) {
        textArray.push(expiredTip);
    }
    const limitPurchaseTip = getLimitPurchaseTip(coupon);
    if (limitPurchaseTip) {
        textArray.push(limitPurchaseTip);
    }
    return textArray;
};
